import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AutomationTemplateKey } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from './automations.module.css';

type TemplateMeta = {
  key: string;
  name: string;
  description: string;
  params: { key: string; label: string; type: string; required?: boolean }[];
};

type Props = {
  projectId: string;
  templates: TemplateMeta[];
  agents: { id: string; name: string; enabled: boolean }[];
  onCreated: (automationId: string) => void;
};

export function AutomationTemplateGallery({ projectId, templates, agents, onCreated }: Props) {
  const qc = useQueryClient();
  const [activeKey, setActiveKey] = useState<AutomationTemplateKey | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});

  const createM = useMutation({
    mutationFn: () =>
      apiFetch<{ automation: { id: string } }>('/api/automations/from-template', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          template_key: activeKey,
          params,
        }),
      }),
    onSuccess: (data) => {
      setActiveKey(null);
      setParams({});
      void qc.invalidateQueries({ queryKey: ['automations', projectId] });
      onCreated(data.automation.id);
    },
  });

  const active = templates.find((t) => t.key === activeKey);

  return (
    <section>
      <h3>Templates</h3>
      <div className={styles.templateGrid}>
        {templates.map((t) => (
          <button
            key={t.key}
            type="button"
            className={styles.templateCard}
            onClick={() => {
              setActiveKey(t.key as AutomationTemplateKey);
              setParams({});
            }}
          >
            <strong>{t.name}</strong>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8125rem', color: 'var(--color-slate)' }}>
              {t.description}
            </p>
          </button>
        ))}
      </div>
      {active ? (
        <div className={styles.stepCard}>
          <h4>{active.name}</h4>
          {active.params.map((p) => (
            <Field key={p.key} label={p.label}>
              {p.key === 'agent_id' ? (
                <select
                  className={leadStyles.filterInput}
                  value={params.agent_id ?? ''}
                  onChange={(e) => setParams({ ...params, agent_id: e.target.value })}
                >
                  <option value="">— agent —</option>
                  {agents
                    .filter((a) => a.enabled)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                </select>
              ) : (
                <TextInput
                  value={params[p.key] ?? ''}
                  onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                />
              )}
            </Field>
          ))}
          <Button type="button" disabled={createM.isPending} onClick={() => createM.mutate()}>
            Workflow aanmaken
          </Button>
        </div>
      ) : null}
    </section>
  );
}
